import { IonContent, IonIcon, IonPage, IonSpinner} from "@ionic/react";
import { ReactNode, useEffect, useState } from "react";
import { withRouter } from "react-router";
import { makeStyles } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import { chevronDownOutline, chevronForwardOutline } from "ionicons/icons";
import Header from "src/components/Header";
import { fetchDocumentosSGC, IDocumento } from "src/services/documentos";
import { fetchFichasTecnicas, IFichaTecnica } from "src/services/fichasTecnicas";

const useStyles = makeStyles(() => ({
  docsFigure: {
    width: "100%",
    maxWidth: "860px",
    margin: "0 auto",
  },
  docsList: {
    listStyle: "none",
    padding: 0,
    margin: "14px auto 0",
    width: "100%",
    maxWidth: "760px",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    columnGap: "12px",
    alignItems: "center",
    position: "relative",
    padding: "10px 0 10px 15px",
    lineHeight: 1.35,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    "&::before": {
      content: "'•'", // Agrega el punto manualmente
      position: "absolute",
      left: "0",
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: "16px",
      color: "black",
    },
  },
  docName: {
    textAlign: "left",
    wordBreak: "break-word",
  },
  link: {
    textDecoration: "none", // Quita el subrayado del enlace
    color: "#900533", // Resalta el enlace en rojo oscuro
    fontWeight: "bold", // Hace que el texto se vea más fuerte
    padding: "4px 8px", // Agrega algo de espacio alrededor para clics más fáciles
    borderRadius: "5px", // Redondea un poco los bordes del enlace
    transition: "background 0.2s", // Efecto suave al pasar el mouse
    whiteSpace: "nowrap",
    justifySelf: "end",
    alignSelf: "center",
    "&:hover": {
      background: "#f8d7da", // Cambia el color de fondo al pasar el mouse
    },
  },
  mensajeFichas: {
    textAlign: "center",
    margin: "18px 0",
  },
  // Separacion entre los desplegables (Fichas Tecnicas / Tabla de Talles)
  fichasFigure: {
    width: "100%",
    maxWidth: "860px",
    margin: "22px auto 0",
  },
  // Cabecera desplegable: barra bordo rellena, mismo color que los
  // encabezados de las tablas de talles.
  fichasToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "10px",
    background: "#900533",
    color: "#fff",
    padding: "10px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    userSelect: "none",
  },
  fichasTitulo: {
    fontStyle: "normal",
    fontWeight: "bold",
    fontSize: "14px",
    lineHeight: 1.3,
    textTransform: "uppercase",
  },
  fichasSubtitulo: {
    display: "block",
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "12px",
    marginTop: "2px",
    opacity: 0.9,
  },
  fichasChevron: {
    fontSize: "22px",
    color: "#fff",
    flexShrink: 0,
  },
  tallesTitle: {
    textAlign: "center",
    fontWeight: "bold",
    margin: "18px 0 8px",
  },
  tallesTable: {
    width: "100%",
    maxWidth: "560px",
    margin: "0 auto",
    borderCollapse: "collapse",
    "& th, & td": {
      border: "1px solid rgba(0, 0, 0, 0.3)",
      padding: "6px 10px",
    },
    "& th": {
      background: "#900533",
      color: "#fff",
      textAlign: "left",
    },
    "& td": {
      textAlign: "right",
    },
    "& td:first-child": {
      textAlign: "left",
    },
  },
}));

const TALLES_PANTALON = [
  { talle: "P", cintura: 76, cadera: 98 },
  { talle: "M", cintura: 80, cadera: 104 },
  { talle: "G", cintura: 86, cadera: 108 },
  { talle: "XG", cintura: 90, cadera: 114 },
  { talle: "XXG", cintura: 96, cadera: 118 },
  { talle: "3XG", cintura: 100, cadera: 122 },
];

const TALLES_CHAQUETA = [
  { talle: "P", busto: 88, cintura: 80, cadera: 90 },
  { talle: "M", busto: 102, cintura: 86, cadera: 104 },
  { talle: "G", busto: 108, cintura: 100, cadera: 108 },
  { talle: "XG", busto: 110, cintura: 104, cadera: 116 },
  { talle: "XXG", busto: 116, cintura: 110, cadera: 120 },
  { talle: "3XG", busto: 120, cintura: 114, cadera: 124 },
  { talle: "6XG", busto: 156, cintura: 150, cadera: 156 },
];

const TALLES_CHOMBA_OPERARIO = [
  { talle: "P", busto: 108, cintura: 104, cadera: 106 },
  { talle: "M", busto: 112, cintura: 110, cadera: 112 },
  { talle: "G", busto: 116, cintura: 114, cadera: 116 },
  { talle: "XL", busto: 124, cintura: 120, cadera: 124 },
  { talle: "XXL", busto: 130, cintura: 128, cadera: 130 },
  { talle: "XXXL", busto: 140, cintura: 140, cadera: 140 },
];

const TALLES_PANTALON_OPERARIO = [
  { talle: "P", cintura: 82, cadera: 106 },
  { talle: "M", cintura: 84, cadera: 108 },
  { talle: "G", cintura: 88, cadera: 114 },
  { talle: "XL", cintura: 94, cadera: 124 },
  { talle: "XXL", cintura: 98, cadera: 128 },
  { talle: "XXXL", cintura: 106, cadera: 132 },
];

interface DesplegableProps {
  titulo: string;
  subtitulo: string;
  abierto: boolean;
  onToggle: () => void;
  children: ReactNode;
}

// Seccion con cabecera bordo que se abre/cierra (Fichas Tecnicas, Tabla de Talles)
const Desplegable: React.FC<DesplegableProps> = ({ titulo, subtitulo, abierto, onToggle, children }) => {
  const classes = useStyles();

  return (
    <div className="d-flex justify-content-center">
      <figure className={classes.fichasFigure}>
        <figcaption
          className={classes.fichasToggle}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggle();
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={abierto}
        >
          <span className={classes.fichasTitulo}>
            {titulo}
            <span className={classes.fichasSubtitulo}>{subtitulo}</span>
          </span>

          <IonIcon
            className={classes.fichasChevron}
            icon={abierto ? chevronDownOutline : chevronForwardOutline}
          />
        </figcaption>

        <Collapse in={abierto} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </figure>
    </div>
  );
};


const Documentaciones: React.FC = () => {
  const classes = useStyles();
  const [documentos, setDocumentos] = useState<IDocumento[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fichas, setFichas] = useState<IFichaTecnica[]>([]);
  const [fichasAbierto, setFichasAbierto] = useState<boolean>(false);
  const [tallesAbierto, setTallesAbierto] = useState<boolean>(false);
  const [loadingFichas, setLoadingFichas] = useState<boolean>(true);
  const [errorFichas, setErrorFichas] = useState<string>("");

  useEffect(() => {
    const obtenerDocumentos = async () => {
      try {
        const data = await fetchDocumentosSGC();
        // AE_Documentos no tiene columna de orden: se ordena por nombre aca
        setDocumentos(
          [...data].sort((a, b) =>
            a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base", numeric: true })
          )
        );
      } catch (error) {
        console.error("Error al obtener documentos:", error);
      } finally {
        setLoading(false);
      }
    };

    obtenerDocumentos();
  }, []);

  // Se cargan al entrar a la pantalla para poder mostrar la cantidad de
  // documentos en la cabecera, aunque la lista arranque contraida.
  useEffect(() => {
    const obtenerFichas = async () => {
      try {
        const data = await fetchFichasTecnicas();
        setFichas(data);
      } catch (error) {
        console.error("Error al obtener fichas tecnicas:", error);
        setErrorFichas("No se pudieron cargar las fichas técnicas. Intente nuevamente.");
      } finally {
        setLoadingFichas(false);
      }
    };

    obtenerFichas();
  }, []);

  // Texto de ayuda de la cabecera: aclara que la seccion se toca para abrirla
  const subtituloFichas = loadingFichas
    ? "Cargando documentos..."
    : errorFichas
      ? "Toque para ver el detalle"
      : `${fichas.length} ${fichas.length === 1 ? "documento" : "documentos"} · toque para ${fichasAbierto ? "ocultar" : "ver"}`;

  const subtituloTalles = `A modo informativo · toque para ${tallesAbierto ? "ocultar" : "ver"}`;

  return (
    <IonPage>
      <Header titulo="Documentaciones" />

      <IonContent id="documentaciones">
        <div className="container mb-3">
          <div className="d-flex justify-content-center">
            <figure className={classes.docsFigure}>
              <figcaption>
                <cite>Documentaciones</cite>
              </figcaption>

                <p className="mt-1 mb-0">
                Es importante para la organización que cada colaborador tenga acceso a los documentos pertinentes para su buen desempeño. A continuación compartimos los manuales, instructivos y tablas.
                </p>
                {loading ? (
            <IonSpinner name="dots" />
          ) : (
            <ul className={classes.docsList}>
              {documentos.map((doc, index) => (
                <li key={index} className={classes.listItem}>
                  <span className={classes.docName}>{doc.nombre}</span>
                  <a href={doc.enlace} className={classes.link} target="_blank" rel="noopener noreferrer">
                    Abrir
                  </a>
                </li>
              ))}
            </ul>
          )}

            </figure>
          </div>

          <Desplegable
            titulo="Fichas Técnicas"
            subtitulo={subtituloFichas}
            abierto={fichasAbierto}
            onToggle={() => setFichasAbierto(!fichasAbierto)}
          >
            <p className="mt-2 mb-0">
              Fichas técnicas y hojas de seguridad (FISPQ) de los productos
              de limpieza. Toque "Abrir" para ver el documento.
            </p>

            {loadingFichas ? (
              <IonSpinner name="dots" />
            ) : errorFichas ? (
              <p className={classes.mensajeFichas}>{errorFichas}</p>
            ) : fichas.length === 0 ? (
              <p className={classes.mensajeFichas}>
                No hay fichas técnicas disponibles por el momento.
              </p>
            ) : (
              <ul className={classes.docsList}>
                {fichas.map((ficha, index) => (
                  <li key={index} className={classes.listItem}>
                    <span className={classes.docName}>{ficha.nombre}</span>
                    <a
                      href={ficha.enlace}
                      className={classes.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Desplegable>

          <Desplegable
            titulo="Tabla de Talles del Uniforme Bordó"
            subtitulo={subtituloTalles}
            abierto={tallesAbierto}
            onToggle={() => setTallesAbierto(!tallesAbierto)}
          >
            <p className="mt-2 mb-0">
              Medidas y talles disponibles del uniforme bordó, a modo informativo.
            </p>

            <p className={classes.tallesTitle}>PANTALON OPERARIA</p>
            <table className={classes.tallesTable}>
              <thead>
                <tr>
                  <th>TALLES</th>
                  <th>CINTURA</th>
                  <th>CADERA</th>
                </tr>
              </thead>
              <tbody>
                {TALLES_PANTALON.map((fila) => (
                  <tr key={fila.talle}>
                    <td>{fila.talle}</td>
                    <td>{fila.cintura}</td>
                    <td>{fila.cadera}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className={classes.tallesTitle}>CHAQUETA OPERARIA</p>
            <table className={classes.tallesTable}>
              <thead>
                <tr>
                  <th>Talles</th>
                  <th>Contorno de Busto</th>
                  <th>Contorno de Cintura</th>
                  <th>Contorno de Cadera</th>
                </tr>
              </thead>
              <tbody>
                {TALLES_CHAQUETA.map((fila) => (
                  <tr key={fila.talle}>
                    <td>{fila.talle}</td>
                    <td>{fila.busto}</td>
                    <td>{fila.cintura}</td>
                    <td>{fila.cadera}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className={classes.tallesTitle}>CHOMBA OPERARIO</p>
            <table className={classes.tallesTable}>
              <thead>
                <tr>
                  <th>Talle</th>
                  <th>Busto</th>
                  <th>Cintura</th>
                  <th>Cadera</th>
                </tr>
              </thead>
              <tbody>
                {TALLES_CHOMBA_OPERARIO.map((fila) => (
                  <tr key={fila.talle}>
                    <td>{fila.talle}</td>
                    <td>{fila.busto}</td>
                    <td>{fila.cintura}</td>
                    <td>{fila.cadera}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className={classes.tallesTitle}>PANTALON OPERARIO</p>
            <table className={classes.tallesTable}>
              <thead>
                <tr>
                  <th>Talle</th>
                  <th>Cintura</th>
                  <th>Cadera</th>
                </tr>
              </thead>
              <tbody>
                {TALLES_PANTALON_OPERARIO.map((fila) => (
                  <tr key={fila.talle}>
                    <td>{fila.talle}</td>
                    <td>{fila.cintura}</td>
                    <td>{fila.cadera}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplegable>

        </div>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(Documentaciones);
